<template>
    <div :class="{'sticky top-20 z-25 w-[inherit] rounded-md bg-white sm:bg-white/80 sm:backdrop-filter sm:backdrop-blur-lg shadow-xs px-1.5 py-2 mt-2 lg:-mt-13': true, 'hidden': selected.length === 0}">
        <div class="grid grid-cols-1 justify-between gap-2 lg:grid-cols-6">
            <div class="col-span-3 flex items-center gap-3">
                <div class="flex items-center gap-1.5">
                    <button @click.prevent="deselectAll" :disabled="isDeletingPhotosList"
                            class="rounded-sm p-1 text-sm font-semibold text-gray-900 hover:shadow-xs hover:bg-gray-100 cursor-pointer">
                        <XMarkIcon class="size-5 text-gray-700" aria-hidden="true" />
                    </button>

                    <span class="text-gray-700 text-sm">{{ $t('photos_list_controls.selected') }}: <strong>{{ selected.length }}</strong></span>
                </div>

                <Menu as="div" class="ml-auto lg:ml-0 relative inline-block text-left" v-if="(!folder || (folder && folder?.slug != 'trash')) && foldersToMove.length !== 0">
                    <div>
                        <MenuButton class="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-gray-100 px-2 py-1.5 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-100 cursor-pointer">
                            <FolderPlusIcon class="size-5 text-gray-400" aria-hidden="true" />
                            {{ $t('photos_list_controls.add_to_folder') }}
                            <ChevronDownIcon20 class="-mr-1 size-5 text-gray-400" aria-hidden="true" />
                        </MenuButton>
                    </div>

                    <transition enter-active-class="transition ease-out duration-100" enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100" leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100" leave-to-class="transform opacity-0 scale-95">
                        <MenuItems class="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                            <div class="py-1">
                                <MenuItem v-for="(_folder, folderIndex) in foldersToMove" :key="`controls-folders-list-${_folder.id}-${folderIndex}`">
                                    <a
                                        href="#"
                                        @click="toFolder = _folder"
                                        :class="[_folder.id == toFolder?.id ? 'bg-gray-100 text-gray-900 outline-hidden' : 'text-gray-700', 'hover:bg-gray-50 block px-4 py-2 text-sm flex items-center gap-2']">
                                        <span>
                                            <svg class="size-1.5" :style="{fill: _folder.color}" viewBox="0 0 6 6" aria-hidden="true">
                                              <circle cx="3" cy="3" r="3" />
                                            </svg>
                                        </span>

                                        {{ _folder.title }}
                                    </a>
                                </MenuItem>
                            </div>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>
            <div class="col-span-3 flex items-center justify-end gap-3">
                <button v-if="folder && folder?.slug != 'trash'"
                        class="w-full lg:w-auto rounded-sm px-2 py-1 text-sm font-medium text-gray-900 bg-white hover:shadow-xs ring-1 ring-gray-100 hover:bg-gray-100 hover:text-gray-900 cursor-pointer flex items-center gap-2 justify-center"
                        @click.prevent="removeFromFolder">
                    <FolderMinusIcon class="size-5 text-gray-400" aria-hidden="true" />
                    {{ $t('photos_list_controls.remove_from_folder') }}
                </button>

                <template v-else>
                    <button v-if="folder && folder?.slug == 'trash'"
                            class="w-full lg:w-auto rounded-sm px-2 py-1 text-sm font-medium text-blue-700 bg-white hover:shadow-xs ring-1 ring-blue-700 hover:bg-blue-700 hover:text-white cursor-pointer"
                            @click.prevent="recoverSelected">
                        {{ $t('photos_list_controls.recover') }}
                    </button>
                    <button v-if="!folder"
                            class="w-full lg:w-auto rounded-sm px-2 py-1 text-sm font-medium text-gray-900 bg-white hover:shadow-xs ring-1 ring-gray-100 hover:bg-gray-100 hover:text-gray-900 cursor-pointer"
                            @click.prevent="deleteAll">
                        {{ $t('photos_list_controls.delete_all') }}
                    </button>
                </template>

                <button
                    @click.prevent="deleteSelected"
                    :disabled="isDeletingPhotosList"
                    class="w-full lg:w-auto justify-center rounded-sm px-2 py-1 text-sm font-medium text-red-700 bg-white hover:shadow-xs ring-1 ring-red-700 hover:bg-red-700 hover:text-white cursor-pointer flex items-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash2" viewBox="0 0 16 16">
                        <path d="M14 3a.702.702 0 0 1-.037.225l-1.684 10.104A2 2 0 0 1 10.305 15H5.694a2 2 0 0 1-1.973-1.671L2.037 3.225A.703.703 0 0 1 2 3c0-1.105 2.686-2 6-2s6 .895 6 2zM3.215 4.207l1.493 8.957a1 1 0 0 0 .986.836h4.612a1 1 0 0 0 .986-.836l1.493-8.957C11.69 4.689 9.954 5 8 5c-1.954 0-3.69-.311-4.785-.793z"/>
                    </svg>

                    {{ $t('photos_list_controls.delete_selected') }}
                </button>
            </div>
        </div>
    </div>

    <DeletePhotosConfirmationModalComponent
        :open="confirmations.deleteAll || confirmations.deleteSelected || confirmations.recoverSelected"
        @close="confirmations.recoverSelected = confirmations.deleteSelected = confirmations.deleteAll = false"
        @confirm-action="confirmationModalAction"
        :title="modalTitle"
        :message="modalMessage"
        :button-text="modalButtonText"
        :isLoading="isDeletingPhotosList || isRecoveringPhotosList"
    />
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import {Folder} from "@/types/GalleryType";
import {Photo} from "@/types/PhotoType";
import {
    ChevronDownIcon as ChevronDownIcon20,
    FolderPlusIcon,
    FolderMinusIcon,
    XMarkIcon
} from "@heroicons/vue/20/solid";

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import DeletePhotosConfirmationModalComponent from "@/components/Galleries/Modals/DeletePhotosConfirmationModalComponent.vue";

export default defineComponent({
    name: "PhotosListControlsComponent",
    components: {
        DeletePhotosConfirmationModalComponent,
        FolderPlusIcon,
        FolderMinusIcon,
        ChevronDownIcon20,
        XMarkIcon,

        Menu, MenuButton, MenuItem, MenuItems,
    },

    props: {
        selected: Array,
        folder: Object,
    },

    data() {
        return {
            toFolder: null as Folder | null,

            confirmations: {
                deleteAll: false,
                deleteSelected: false,
                recoverSelected: false,
            }
        };
    },

    emits: ['remove-selected', 'deselected-all'],

    watch: {
        toFolder: function () {
            if(this.toFolder) {
                this.moveToFolder({
                    photos: this.selected,
                    folder: this.toFolder.slug,
                }).then(() => {
                    this.successAlert({
                        message: this.$t('photos_list_controls.success.added_to_folder', {
                            folder: this.toFolder?.title
                        })
                    });

                    this.toFolder = null;

                    this.$emit('remove-selected');
                });
            }
        }
    },

    computed: {
        ...mapGetters({
            folders: "galleries/folders",

            gallery: "galleries/gallery",

            isDeletingPhotosList: "photos/isDeletingItemsList",
            isRecoveringPhotosList: "photos/isRecoveringDeletedItemsList"
        }),

        currentSlug(): string {
            return this.$route.params.gallery as string;
        },

        foldersToMove(): Array<Folder> {
            if(!this.folder) {
                return this.folders;
            }

            return this.folders.filter((item: Folder) => {
                return item.id !== (this.folder as Folder).id;
            })
        },

        modalTitle(): string {
            if(this.confirmations.deleteAll) {
                return this.$t('photos_list_controls.confirmations.title.delete_all');
            }

            if(this.confirmations.recoverSelected) {
                return this.$t('photos_list_controls.confirmations.title.recover_selected');
            }

            return this.$t('photos_list_controls.confirmations.title.delete_selected');
        },

        modalMessage(): string {
            if(this.confirmations.deleteAll) {
                return this.$t('photos_list_controls.confirmations.message.delete_all');
            }

            if(this.confirmations.recoverSelected) {
                return this.$t('photos_list_controls.confirmations.message.recover_selected', {
                    count: this.selected?.length
                });
            }

            return this.$t('photos_list_controls.confirmations.message.delete_selected', {
                count: this.selected?.length
            });
        },

        modalButtonText(): string {
            if(this.confirmations.deleteAll) {
                return this.$t('photos_list_controls.confirmations.button.delete_all');
            }

            if(this.confirmations.recoverSelected) {
                return this.$t('photos_list_controls.confirmations.button.recover_selected');
            }

            return this.$t('photos_list_controls.confirmations.button.delete_selected');
        }
    },

    methods: {
        ...mapActions({
            moveToFolder: "photos/moveToFolder",
            deletePhotosList: "photos/deleteList",
            recoverDeletedList: "photos/recoverDeletedList",

            loadFolders: "galleries/loadFolders",

            removePhotosFromFolder: "photos/removeFromFolder",

            successAlert: "alerts/success",
            errorAlert: "alerts/error"
        }),

        confirmationModalAction() {
            if(this.confirmations.deleteAll) {
                this.deleteAll();

                return;
            }

            if(this.confirmations.deleteSelected) {
                this.deleteSelected();

                return;
            }

            if(this.confirmations.recoverSelected) {
                this.recoverSelected();

                return;
            }
        },

        removeFromFolder() {
            if(this.folder) {
                this.removePhotosFromFolder({
                    photos: this.selected as Photo[],
                    folder: this.folder.slug
                }).then(() => {
                    this.$emit('remove-selected');

                    this.successAlert({
                        message: this.$t('photos_list_controls.success.removed_from_folder')
                    });
                }).catch((reason) => {
                    console.log(reason);

                    this.errorAlert({
                        message: this.$t('photos_list_controls.error.removing_from_folder')
                    });
                });
            }
        },

        deleteAll() {
            if(this.isDeletingPhotosList) {
                return;
            }

            if(!this.confirmations.deleteAll) {
                this.confirmations.deleteAll = true;
                return;
            }

            this.deletePhotosList({
                photos: [],
                gallery: this.currentSlug
            }).then(() => {
                this.$emit('remove-selected');

                this.confirmations.deleteAll = false;

                this.successAlert({
                    message: this.$t('photos_list_controls.success.files_deleted')
                });
            }).catch(() => {
                this.confirmations.deleteAll = false;

                this.errorAlert({
                    message: this.$t('photos_list_controls.error.delete_all')
                });
            });
        },

        deleteSelected() {
            if(this.isDeletingPhotosList) {
                return;
            }

            if(!this.confirmations.deleteSelected) {
                this.confirmations.deleteSelected = true;
                return;
            }

            this.deletePhotosList({
                photos: this.selected as Photo[],
                gallery: this.currentSlug
            }).then(() => {
                this.$emit('remove-selected');

                this.confirmations.deleteSelected = false;

                this.successAlert({
                    message: this.$t('photos_list_controls.success.files_deleted')
                });
            }).catch(() => {
                this.confirmations.deleteSelected = false;

                this.errorAlert({
                    message: this.$t('photos_list_controls.error.delete_selected')
                });
            });
        },

        recoverSelected() {
            if(this.isRecoveringPhotosList) {
                return;
            }

            if(!this.confirmations.recoverSelected) {
                this.confirmations.recoverSelected = true;
                return;
            }

            this.recoverDeletedList({
                photos: this.selected as Photo[],
                gallery: this.currentSlug
            }).then(() => {
                this.$emit('remove-selected');

                this.loadFolders({gallery: this.currentSlug});

                this.confirmations.recoverSelected = false;

                this.successAlert({
                    message: this.$t('photos_list_controls.success.files_recovered')
                });
            }).catch(() => {
                this.confirmations.recoverSelected = false;

                this.errorAlert({
                    message: this.$t('photos_list_controls.error.recover_selected')
                });
            });
        },

        deselectAll() {
            if(this.isDeletingPhotosList) {
                return;
            }

            this.$emit('remove-selected');
        }
    }
})
</script>

<style lang="scss" scoped>

</style>